import { TelemetryEventType } from './telemetry-models';
import { BaseTracker } from './base-tracker';
import type { ReportingEvent, TelemetryEvent } from './telemetry-models';

/**
 * Tracks browser reports using the Reporting API
 * Captures deprecations, interventions, crashes, and other browser warnings
 */
export class ReportingTracker extends BaseTracker {
  private _observer: ReportingObserver | null = null;
  private _isSupported: boolean;

  constructor(onEvent: (event: TelemetryEvent) => void, enabledLogging = false) {
    super(onEvent, enabledLogging);
    this._isSupported = typeof ReportingObserver !== 'undefined';
  }

  /**
   * Start tracking browser reports
   */
  public start(): void {
    if (!this._isSupported) {
      this._log('ReportingObserver not supported in this browser');
      return;
    }

    try {
      this._observer = new ReportingObserver(
        (reports) => {
          for (const report of reports) {
            this._handleReport(report);
          }
        },
        { buffered: true }
      );

      this._observer.observe();
      this._log('Reporting tracking started');
    } catch (error) {
      this._log('ERROR: Failed to setup ReportingObserver', error);
    }
  }

  /**
   * Stop tracking reports
   */
  public stop(): void {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
      this._log('Reporting tracking stopped');
    }
  }

  private _handleReport(report: Report): void {
    const reportData: ReportingEvent = {
      type: report.type,
      url: report.url,
      timestamp: Date.now(),
      body: this._sanitizeBody(report.body),
    };

    this._onEvent({ type: TelemetryEventType.REPORTING, data: reportData });
  }

  private _sanitizeBody(body: ReportBody | null): Record<string, unknown> {
    if (!body) return {};

    const sanitized: Record<string, unknown> = {};

    // ReportBody can be DeprecationReportBody, InterventionReportBody, etc.
    // Extract common properties safely
    try {
      const bodyObj = body as unknown as Record<string, unknown>;

      // Common properties across report types
      if ('id' in bodyObj) sanitized.id = bodyObj.id;
      if ('message' in bodyObj) sanitized.message = bodyObj.message;
      if ('sourceFile' in bodyObj) sanitized.sourceFile = bodyObj.sourceFile;
      if ('lineNumber' in bodyObj) sanitized.lineNumber = bodyObj.lineNumber;
      if ('columnNumber' in bodyObj) sanitized.columnNumber = bodyObj.columnNumber;

      // Deprecation-specific
      if ('anticipatedRemoval' in bodyObj) sanitized.anticipatedRemoval = bodyObj.anticipatedRemoval;

      // CSP violation-specific
      if ('blockedURL' in bodyObj) sanitized.blockedURL = bodyObj.blockedURL;
      if ('disposition' in bodyObj) sanitized.disposition = bodyObj.disposition;
      if ('effectiveDirective' in bodyObj) sanitized.effectiveDirective = bodyObj.effectiveDirective;
      if ('violatedDirective' in bodyObj) sanitized.violatedDirective = bodyObj.violatedDirective;

      // Crash-specific
      if ('reason' in bodyObj) sanitized.reason = bodyObj.reason;
    } catch (error) {
      this._log('ERROR: Failed to sanitize report body', error);
    }

    return sanitized;
  }
}
